/**
 * Authentication validation schemas using Zod
 * Provides type-safe validation for login and registration forms
 */

import { z } from 'zod';

// Login form schema
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'auth.login.validation.emailRequired')
    .email('auth.login.validation.emailInvalid'),
  password: z.string().min(1, 'auth.login.validation.passwordRequired'),
  rememberMe: z.boolean().optional(),
});

// Registration form schema
export const registerSchema = z
  .object({
    firstName: z.string().min(1, 'auth.register.validation.firstNameRequired'),
    lastName: z.string().min(1, 'auth.register.validation.lastNameRequired'),
    email: z
      .string()
      .min(1, 'auth.register.validation.emailRequired')
      .email('auth.register.validation.emailInvalid'),
    phone: z
      .string()
      .min(1, 'auth.register.validation.phoneRequired')
      .regex(/^\+?[\d\s-()]+$/, 'auth.register.validation.phoneInvalid'),
    password: z
      .string()
      .min(8, 'auth.register.validation.passwordMinLength')
      .regex(/[A-Z]/, 'auth.register.validation.passwordUppercase')
      .regex(/[a-z]/, 'auth.register.validation.passwordLowercase')
      .regex(/[0-9]/, 'auth.register.validation.passwordNumber')
      .regex(/[@$!%*?&]/, 'auth.register.validation.passwordSpecial'),
    confirmPassword: z.string(),
    acceptTerms: z.boolean().refine(val => val === true, 'auth.register.validation.termsRequired'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'auth.register.validation.passwordMismatch',
    path: ['confirmPassword'],
  });

// Password reset request schema
export const passwordResetRequestSchema = z
  .object({
    method: z.enum(['email', 'phone']),
    email: z.string().optional(),
    phone: z.string().optional(),
  })
  .refine(
    data => {
      if (data.method === 'email') {
        return data.email && z.string().email().safeParse(data.email).success;
      }
      if (data.method === 'phone') {
        return data.phone && data.phone.length >= 10;
      }
      return false;
    },
    {
      message: 'Please provide valid contact information',
      path: ['email', 'phone'],
    }
  );

// Password reset schema
export const passwordResetSchema = z
  .object({
    password: z
      .string()
      .min(8, 'auth.register.validation.passwordMinLength')
      .regex(/[A-Z]/, 'auth.register.validation.passwordUppercase')
      .regex(/[a-z]/, 'auth.register.validation.passwordLowercase')
      .regex(/[0-9]/, 'auth.register.validation.passwordNumber')
      .regex(/[@$!%*?&]/, 'auth.register.validation.passwordSpecial'),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'auth.register.validation.passwordMismatch',
    path: ['confirmPassword'],
  });

// Type exports
export type LoginFormData = z.infer<typeof loginSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type PasswordResetRequestData = z.infer<typeof passwordResetRequestSchema>;
export type PasswordResetData = z.infer<typeof passwordResetSchema>;
